import Guid from "_shared/guids/guid";
import EntityAgreementAccessSchema, {
  EntityAgreementAccessParams,
  EntityAgreementAccessJSON,
} from "permissions/resources/legal-entities/_schemas/entity-agreement-access-schema";
import LegalEntitiesPlatformAccessSchema, {
  LegalEntitiesPlatformAccessJSON,
  LegalEntitiesPlatformAccessParams,
} from "permissions/resources/legal-entities/_schemas/legal-entities-platform-access-schema";
import LegalEntityAccessSchema, {
  LegalEntityAccessParams,
  LegalEntityAccessJSON,
} from "permissions/resources/legal-entities/_schemas/legal-entity-access-schema";
import { LegalEntitiesPlatformAccessType, LegalEntityAccessType } from "permissions/resources/legal-entities/legal-entity-access-types";
import { Resource } from "permissions/resources/permission-resource";
import { registerResource } from "permissions/resources/resource-registry";
import { AccessLevel, AnyResourceJSON, ResourceFromJSON, ResourceType, Scope } from "permissions/resources/permission-resource-types";

export class LegalEntitiesPlatformAccess extends Resource {
  public static create(params?: LegalEntitiesPlatformAccessParams): LegalEntitiesPlatformAccess {
    const parsed = LegalEntitiesPlatformAccessSchema.validate(params);
    return new LegalEntitiesPlatformAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const inputJson = json as LegalEntitiesPlatformAccessJSON;
    const parsed = LegalEntitiesPlatformAccessSchema.deserialize(inputJson);
    return new LegalEntitiesPlatformAccess(parsed) as ResourceFromJSON<J>;
  }

  public readonly resourceType = ResourceType.LegalEntitiesPlatformAccess;
  public readonly scope: Scope;
  public readonly accessType: LegalEntitiesPlatformAccessType;

  public constructor(params: LegalEntitiesPlatformAccessParams) {
    super(params);
    this.accessType = params.accessType;
    this.scope = params.scope;
  }

  public toJSON(): LegalEntitiesPlatformAccessJSON {
    return LegalEntitiesPlatformAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `LegalEntitiesPlatformAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? "true" : "false"}
      resourceTypeText: ${this.resourceTypeText}
      accessType: ${this.accessType}
    }`;
  }
}

registerResource("LegalEntitiesPlatformAccess", (json) => LegalEntitiesPlatformAccess.fromJSON(json));

export class EntityAgreementAccess extends Resource {
  public static create(params?: EntityAgreementAccessParams): EntityAgreementAccess {
    const parsed = EntityAgreementAccessSchema.validate(params);
    return new EntityAgreementAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = EntityAgreementAccessSchema.deserialize(json as EntityAgreementAccessJSON);
    return new EntityAgreementAccess(parsed) as ResourceFromJSON<J>;
  }

  public readonly resourceType = ResourceType.EntityAgreementAccess;
  public readonly entityAgreementId?: Guid;
  public readonly accessLevel?: AccessLevel;

  public constructor(params?: Omit<EntityAgreementAccessParams, "resourceType">) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.entityAgreementId = params.entityAgreementId;
    }
  }

  public toJSON(): EntityAgreementAccessJSON {
    return EntityAgreementAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `EntityAgreementAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? "true" : "false"}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      entityAgreementId: ${this.entityAgreementId}
    }`;
  }
}

registerResource("EntityAgreementAccess", (json) => EntityAgreementAccess.fromJSON(json));

export class LegalEntityAccess extends Resource {
  public static create(params?: LegalEntityAccessParams): LegalEntityAccess {
    const parsed = LegalEntityAccessSchema.validate(params);
    return new LegalEntityAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = LegalEntityAccessSchema.deserialize(json as LegalEntityAccessJSON);
    return new LegalEntityAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): LegalEntityAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      [
        "scope",
        Object.values(Scope)
          .filter((key) => !Number.isNaN(Number(key)))
          .map((value) => Number(value)),
      ],
      [
        "accessType",
        Object.values(LegalEntityAccessType)
          .filter((key) => !Number.isNaN(Number(key)))
          .map((value) => Number(value)),
      ],
      [
        "accessLevel",
        Object.values(AccessLevel)
          .filter((key) => !Number.isNaN(Number(key)))
          .map((value) => Number(value)),
      ],
    ];
    return this.generatePermutations(LegalEntityAccess, fieldIterations) as LegalEntityAccess[];
  }

  public readonly resourceType = ResourceType.LegalEntityAccess;
  public readonly scope: Scope;
  public readonly accessType: LegalEntityAccessType;
  public readonly accessLevel: AccessLevel;

  public constructor(params?: LegalEntityAccessParams) {
    super(params);
    this.scope = params?.scope ?? Scope["Need-to-Know"];
    this.accessLevel = params?.accessLevel ?? AccessLevel["Read-Only"];
    this.accessType = params?.accessType ?? LegalEntityAccessType["Member Information"];
  }

  public toJSON(): LegalEntityAccessJSON {
    return LegalEntityAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `LegalEntityAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? "true" : "false"}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      accessType: ${this.accessType}
    }`;
  }
}

registerResource("LegalEntityAccess", (json) => LegalEntityAccess.fromJSON(json));
